import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Container } from '../styles/texts'
import Table from '../structure/Table/Table'
import TableRow from '../structure/Table/TableRow'
import TableHeader from '../structure/Table/TableHeader'
import Breadcrumbs from '../structure/Breadcrumbs'

import EmptyState from '../components/EmptyState'
import ActivityPanel from '../sections/ActivityPanel'
import ActivityItem from '../sections/ActivityItem'
import { Heading1 } from '../styles/texts'
import { Skeleton } from '../styles/general'
import useSolanaActivity from '../hooks/useSolanaActivity'
import useSolanaConfig from '../hooks/useSolanaConfig'
import { getSolscanLink } from '../utils/web3-utils'
import externalLink from '../assets/external-link.svg'


const SolanaActivity = ({ width }) => {
  const params = useParams()
  const navigate = useNavigate()
  const [daysAgo, setDaysAgo] = useState(30)
  const [isOpen, setOpen] = useState(false)
  const [item, setItem] = useState(null)
  const [configExpanded, setConfigExpanded] = useState(false)
  const [selectedClientIndex, setSelectedClientIndex] = useState(0)
  const { data, isLoading, isLoadingMore, isError, hasMore, loadMore } = useSolanaActivity(params.id, daysAgo || 30)
  const { data: configData, isLoading: configLoading } = useSolanaConfig(params.id)

  // Get selected client data
  const selectedClient = configData && configData.length > 0 ? configData[selectedClientIndex] : null

  // eslint-disable-next-line no-unused-vars
  const [filters, setFilters] = useState({})
  const location = useLocation()




  const updateURL = (newFilters) => {
    const searchParams = new URLSearchParams()
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) {
        searchParams.set(key, newFilters[key])
      } else {
        searchParams.delete(key)
      }
    })
    const queryString = searchParams.toString()
    const newURL = queryString ? `${location.pathname}?${queryString}` : location.pathname

    navigate(newURL, { replace: true })
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const daysAgo = searchParams.get('daysAgo') || 30
    setFilters({ daysAgo })
    setDaysAgo(daysAgo)

    updateURL({ daysAgo })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])



  const handleClick = (isOpen, item) => {
    setItem(item)
    setOpen(isOpen)
  }



  const handleDaysAgo = (days) => {
    setDaysAgo(days)
    updateURL({ ...filters, daysAgo: days })
  }

  const filterClientSwaps = (swap) => {
    return swap.feeCollector === selectedClient.feeCollector[0]._id && swap.succeeded
  }

  const getTitle = (daysAgo) => {
    switch (daysAgo) {
      case 1:
        return 'No Solana logs to report in the last day just yet.'
      case 7:
        return 'No Solana logs to report in the last week just yet.'
      case 30:
      case 31:
        return 'No Solana logs to report in the last month just yet.'
      default:
        return 'No Solana logs to report in the selected period just yet.'
    }
  }

  return (
    <ActivitySection>
      <Container>
        <Breadcrumbs />
        <TitleRow>
          <Heading1>Solana Activity</Heading1>
          {configLoading ? (
            <Skeleton height="16px" width="200px" />
          ) : configData && configData.length > 0 && (
            <ClientSelector>
              <ConfigLabel>Fee Collector:</ConfigLabel>
              <ClientDropdown
                value={selectedClientIndex}
                onChange={(e) => setSelectedClientIndex(parseInt(e.target.value))}
              >
                {configData.map((client, index) => (
                  <option key={client._id} value={index}>
                    {client.namespace} ({client.vaultPdaAuth?.slice(0, 8)}...)
                  </option>
                ))}
              </ClientDropdown>
            </ClientSelector>
          )}
        </TitleRow>
        
        {/* Configuration section */}
        {configData && configData.length > 0 && (
          <ConfigSection>

            {/* Level 1: Fee Collector and Account */}
            <ConfigLevel1>
              <ConfigItem>
                <ConfigLabel>Fee Collector:</ConfigLabel>
                <ConfigValue>{selectedClient?.namespace || 'N/A'}</ConfigValue>
              </ConfigItem>
              <ConfigItem>
                <ConfigLabel>Account:</ConfigLabel>
                <ConfigValueWithLink>
                  <ConfigValue>{selectedClient?.vaultPdaAuth || 'N/A'}</ConfigValue>
                  <ExternalLinkIcon 
                    src={externalLink} 
                    alt="Open in Solscan"
                    onClick={() => selectedClient?.vaultPdaAuth && window.open(getSolscanLink(selectedClient.vaultPdaAuth, 'account'), '_blank')}
                  />
                </ConfigValueWithLink>
              </ConfigItem>
              <ExpandButton onClick={() => setConfigExpanded(!configExpanded)}>
                {configExpanded ? 'Show less ▲' : 'Show more ▼'}
              </ExpandButton>
            </ConfigLevel1>
            
            {/* Level 2: Additional details */}
            {configExpanded && (
              <ConfigLevel2>
                <ConfigItem>
                  <ConfigLabel>Withdraw Account:</ConfigLabel>
                  <ConfigValue>{selectedClient?.accountOut || 'N/A'}</ConfigValue>
                </ConfigItem>
                <ConfigItem>
                  <ConfigLabel>Threshold:</ConfigLabel>
                  <ConfigValue>{selectedClient?.feeCollector?.[0]?.threshold || 'N/A'}</ConfigValue>
                </ConfigItem>
                <ConfigItem>
                  <ConfigLabel>Token Out:</ConfigLabel>
                  <ConfigValue>{selectedClient?.feeCollector?.[0]?.tokenOut || 'N/A'}</ConfigValue>
                </ConfigItem>
              </ConfigLevel2>
            )}
          </ConfigSection>
        )}
        {isError && (
          <ErrorMessage>
            <p>⚠️ There was an error fetching Solana task data. Please try again later.</p>
          </ErrorMessage>
        )}
        {isLoading ? (
          <>
            <br />
            <br />
            <Skeleton height="24px" width="100%" />
          </>
        ) : data.filter(filterClientSwaps).length > 0 ? (
          <>

            <TableWrapper>
              <ContainerTable
                header={
                  <TableRow>
                    <TableHeader title="When" align="left" />
                    <TableHeader title="Network" align="left" />
                    <TableHeader title="Task" align="left" />
                    <TableHeader title="Amount" align="left" />
                  </TableRow>
                }
              >
                {data
                  ?.filter(filterClientSwaps)
                  .map((swap, i) => (
                    <ActivityItem
                      key={i}
                      item={swap}
                      index={i + 1}
                      width={width}
                      hideCost
                      handleClick={handleClick}
                    />
                  ))}
              </ContainerTable>
            </TableWrapper>
            <ActivityPanel
              open={isOpen}
              item={item}
              onClose={() => setOpen(!isOpen)}
            />
          </>
        ) : (
          <EmptyState
            width={width}
            title={getTitle(daysAgo)}
            text="Solana data will appear here once transactions are executed."
          />
        )}
        <PaginationContainer>
          <Chip onClick={(e) => handleDaysAgo(7)}>Week</Chip>
          <Chip onClick={(e) => handleDaysAgo(31)}>Month</Chip>
          {hasMore && (
            <Chip onClick={loadMore} disabled={isLoadingMore}>
              {isLoadingMore ? 'Loading...' : 'Load more'}
            </Chip>
          )}
        </PaginationContainer>
      </Container>
    </ActivitySection>
  )
}

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media only screen and (max-width: 800px) {
    white-space: nowrap;
  }
`
const ErrorMessage = styled.div`
  background: rgba(255, 69, 58, 0.1);
  color: #ff453a;
  border: 1px solid #ff453a;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  font-size: 16px;
  margin: 20px 0;
`

const ContainerTable = styled(Table)`
  background-color: rgba(168, 154, 255, 0.12);
  border-radius: 24px;
  tbody tr:hover {
    background: rgba(0, 0, 0, 0.24);
  }
  th {
    padding-top: 10px;
    padding-bottom: 10px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.50);
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }
  td:first-child,
  th:first-child {
    padding-left: 25px;
    white-space: nowrap;
  }
  td:last-child,
  th:last-child {
    padding-right: 25px;
  }
  td {
    padding: 23px 15px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    vertical-align: top;
  }
  tr:last-child td {
    border-bottom: 0px solid;
  }
`

const ActivitySection = styled.section`
  height: auto;
  padding: 20px 0;
  color: ${(props) => props.theme.textWhite};
  width: 100%;
  margin: auto;

  .custom-select-container {
    position: relative;
    display: inline-block;
  }

  .custom-select {
    padding: 6px 13px 6px 16px;
    min-width: 150px;
    border: 0px;
    background: rgba(168, 154, 255, 0.10);
    color: white;
    border-radius: 10px;
    font-family: 'DMSans';
    font-size: 17px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px;
    outline: none !important;
    margin-bottom: 30px;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }

  .arrow-container {
    position: absolute;
    transform: translateY(-50%);
    top: 35%;
    right: 13px;
  }

  .arrow-container img {
    height: 15px;
    width: 15px;
    object-fit: contain;
    height: 100%;
  }
`



const ConfigSection = styled.div`
  background: rgba(168, 154, 255, 0.08);
  border: 1px solid rgba(168, 154, 255, 0.2);
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
`

const ConfigLevel1 = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

const ConfigLevel2 = styled.div`
  border-top: 1px solid rgba(168, 154, 255, 0.2);
  margin-top: 16px;
  padding-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
`

const ConfigValueWithLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`

const ExternalLinkIcon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  filter: brightness(0) saturate(100%) invert(76%) sepia(45%) saturate(4158%) hue-rotate(228deg) brightness(95%) contrast(89%);
  
  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`

const ExpandButton = styled.button`
  background: rgba(168, 154, 255, 0.15);
  border: 1px solid rgba(168, 154, 255, 0.3);
  color: #A288DF;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(168, 154, 255, 0.25);
    border-color: rgba(168, 154, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    align-self: flex-start;
  }
`

const ConfigItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`

const ConfigLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 500;
  min-width: 120px;
  
  @media (max-width: 768px) {
    min-width: auto;
  }
`

const ConfigValue = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  background: rgba(168, 154, 255, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
  word-break: break-all;
  flex: 1;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const ClientSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

const ClientDropdown = styled.select`
  background: rgba(168, 154, 255, 0.1);
  border: 1px solid rgba(168, 154, 255, 0.3);
  color: #ffffff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  min-width: 250px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(168, 154, 255, 0.15);
    border-color: rgba(168, 154, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #A288DF;
    box-shadow: 0 0 0 2px rgba(168, 154, 255, 0.2);
  }
  
  option {
    background: #1a1a1a;
    color: #ffffff;
  }
  
  @media (max-width: 768px) {
    min-width: 200px;
    flex: 1;
  }
`

const PaginationContainer = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 30px;
  gap: 30px;
`

const Chip = styled.span`
  border-radius: 24px;
  background: #6F5CE6;
  padding: 7px 12px;
  cursor: pointer;
  &:hover {
    background: #5042A5;
  }
`

export default SolanaActivity